
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import ArticleCadPreview from "@/components/ArticleCadPreview.vue";
import { TicketDto } from "@/dtos/ticketDto";
import { WorkStepDto } from "@/dtos/workStepDto";
import { BlexonResolutionType, blexonResolutionTypes } from "@/dtos/blexonResolutionType";
import { ComplaintDto } from "@/dtos/complaintDto";

@Component({
	components: {
		ArticleCadPreview,
	},
})
export default class CostDistribution extends Vue {
	@Prop()
	private ticket!: TicketDto;
	private loading: boolean = false;
	private worksteps: WorkStepDto[] = [];
	private valid: boolean = true;
	private error: number = 0;
	private rules = {
		validShare: (complain: ComplaintDto) => (value: string) => {
			var cost = this.getManufacturerPrice(complain);
			const sum = +complain.manufacturerShare + +complain.powderCoaterShare;
			if (
				complain.blexonResolution == BlexonResolutionType.AcceptedErsatzlieferung ||
				complain.blexonResolution == BlexonResolutionType.AcceptedNacharbeit
			) {
				if (sum > cost.total + 0.009) {
					return "Der Betrag ist höher als die Herstellung kostet. Kann das sein?";
				}
			}
			if (complain.blexonResolution == BlexonResolutionType.AcceptedDiscount) {
				const maxDiscount =
					complain.part.customerPricePerPart * complain.numberOfWrongParts;
				if (complain.schadenssumme > maxDiscount + 0.009) {
					return "Der Fertiger zahlt mehr als die Herstellung gekostet hat. Kann das sein?";
				}
			}
			return true;
		},
	};

	@Watch("valid")
	private validChanged() {
		this.$emit("validChanged", this.valid);
	}

	// Wessen Fehler ist es?
	private errorChanged(complaint: ComplaintDto) {
		const cost = this.getManufacturerPrice(complaint);
		const damage = Math.min(cost.total, complaint.schadenssumme);
		if (complaint.blexonResolution == BlexonResolutionType.AcceptedDiscount) {
			// If a discount was accepted, simply subtract the specified amount for the guilty party
			complaint.manufacturerShare = 0;
			complaint.powderCoaterShare = 0;
			complaint.hotDipZincCoaterShare = 0;
			complaint.galvanizeZincCoaterShare = 0;
			switch (this.error) {
				case 1:
					// Fertiger is at fault
					complaint.manufacturerShare = -damage;
					break;
				case 2:
					// Powder coater is at fault
					complaint.powderCoaterShare = -damage;
					break;
				case 3:
					// Feuerverzinker is at fault
					complaint.hotDipZincCoaterShare = -damage;
					break;
				case 4:
					// Galvanisch-Verzinker is at fault
					complaint.galvanizeZincCoaterShare = -damage;
					break;
				default:
					console.log("Invalid guilty party.");
			}
		} else {
			// Otherwise, the parts have to be produced again.
			// The guilty party doesn't get paid to produce the parts again and in addition
			// they have to pay the work of the other parties.
			// The not guilty parties are compensated to produce the parts again

			// Simplify by first assigning each party the price of their own work as a positive amount.
			const manufacturerPrice =
				Math.round((cost.materialPrice + cost.fertigungPrice + Number.EPSILON) * 100) / 100;
			complaint.manufacturerShare = manufacturerPrice;
			complaint.powderCoaterShare = cost.powderCoatingPrice;
			complaint.hotDipZincCoaterShare = cost.hotDipZincCoatingPrice;
			complaint.galvanizeZincCoaterShare = cost.galvanizeZincCoatingPrice;
			// Then, the guilty party has to pay these amounts and doesn't get compensated for their work themselves.
			switch (this.error) {
				case 1:
					// Fertiger is at fault.
					complaint.manufacturerShare = -(
						cost.powderCoatingPrice +
						cost.hotDipZincCoatingPrice +
						cost.galvanizeZincCoatingPrice
					);
					break;
				case 2:
					// Powder coater is at fault
					complaint.powderCoaterShare = -(
						manufacturerPrice +
						cost.hotDipZincCoatingPrice +
						cost.galvanizeZincCoatingPrice
					);
					break;
				case 3:
					// Hotdip zinc coater is at fault
					complaint.hotDipZincCoaterShare = -(
						manufacturerPrice +
						cost.powderCoatingPrice +
						cost.galvanizeZincCoatingPrice
					);
					break;
				case 4:
					// Galvanize zinc coater is at fault
					complaint.galvanizeZincCoaterShare = -(
						manufacturerPrice +
						cost.hotDipZincCoatingPrice +
						cost.powderCoatingPrice
					);
					break;
				default:
					console.log("Invalid guilty party.");
			}
		}
		this.updateManufacturerShare(complaint);
		this.updatePowderCoaterShare(complaint);
		this.updateHotDipZincCoaterShare(complaint);
		this.updateGalvanizeZincCoaterShare(complaint);
	}

	private validate() {
		(this.$refs.costForm as any).validate();
		this.$emit("validChanged", this.valid);
	}

	private async clearPreviousCalculation() {
		var response = await httpClient().post(
			`Ticket/ClearPreviousCalculation?ticketId=${this.ticket.id}`
		);
	}

	private async calculateCost() {
		try {
			this.loading = true;
			var response = await httpClient().post(
				`Ticket/GetDetailedCostsOfParts?ticketId=${this.ticket.id}`
			);
			this.worksteps = response.data;
			this.validate();
		} finally {
			this.loading = false;
		}
	}

	private async updateManufacturerShare(complain: ComplaintDto) {
		await httpClient().post(
			`Ticket/updateManufacturerShare?complainId=${complain.id}&manufacturerShare=${complain.manufacturerShare}`
		);
	}

	private async updatePowderCoaterShare(complain: ComplaintDto) {
		await httpClient().post(
			`Ticket/updatePowderCoaterShare?complainId=${complain.id}&powderCoaterShare=${complain.powderCoaterShare}`
		);
	}
	private async updateHotDipZincCoaterShare(complain: ComplaintDto) {
		await httpClient().post(
			`Ticket/UpdateHotDipZincCoaterShare?complainId=${complain.id}&share=${complain.hotDipZincCoaterShare}`
		);
	}
	private async updateGalvanizeZincCoaterShare(complain: ComplaintDto) {
		await httpClient().post(
			`Ticket/UpdateGalvanizeZincCoaterShare?complainId=${complain.id}&share=${complain.galvanizeZincCoaterShare}`
		);
	}

	private async updateDiscount(complain: ComplaintDto) {
		await httpClient().post(
			`Ticket/updateDiscount?complainId=${complain.id}&discount=${complain.schadenssumme}`
		);
	}

	private getResolutionDisplayText(blexonResolutionType: BlexonResolutionType): string {
		return blexonResolutionTypes.filter((r) => r.value == blexonResolutionType)[0].name;
	}

	private getCustomerPrice(complain: ComplaintDto) {
		var customerPrice = 0;
		if (complain.blexonResolution == BlexonResolutionType.AcceptedDiscount) {
			customerPrice = complain.part.customerPricePerPart * complain.numberOfWrongParts;
		} else {
			var worksteps = this.worksteps.filter(
				(w) =>
					w.articleId == complain.part.articleId ||
					complain.part.includedSubarticleIds.some((id) => id === w.articleId)
			);
			worksteps.forEach((w) => (customerPrice += w.cost * complain.numberOfWrongParts));
		}
		return customerPrice;
	}

	private getBlexonCost() {
		let cost = 0;

		for (const complain of this.ticket.complains) {
			var manufacturingCost = this.getManufacturerPrice(complain);
			switch (complain.blexonResolution) {
				case BlexonResolutionType.AcceptedDiscount:
					var effektiverBetrag = Math.min(
						+manufacturingCost.total,
						+complain.schadenssumme
					);
					cost +=
						effektiverBetrag +
						+complain.manufacturerShare +
						+complain.powderCoaterShare +
						+complain.hotDipZincCoaterShare +
						+complain.galvanizeZincCoaterShare;
					break;
				case BlexonResolutionType.AcceptedErsatzlieferung:
				case BlexonResolutionType.AcceptedNacharbeit:
					cost +=
						+complain.manufacturerShare +
						+complain.powderCoaterShare +
						+complain.hotDipZincCoaterShare +
						+complain.galvanizeZincCoaterShare;
					break;
				default:
			}
		}
		return cost;
	}

	private getCustomerCost() {
		let cost = 0;

		for (const complain of this.ticket.complains) {
			var manufacturingCost = this.getManufacturerPrice(complain);
			switch (complain.blexonResolution) {
				case BlexonResolutionType.AcceptedDiscount:
					var effektiverBetrag = this.getCustomerPrice(complain);
					cost += effektiverBetrag - +complain.schadenssumme;
					break;
				case BlexonResolutionType.AcceptedErsatzlieferung:
				case BlexonResolutionType.AcceptedNacharbeit:
					cost += 0; // immer 0. Sonst muss man dem Kunden eine Gutschrift geben.
					break;
				default:
					console.log("Resolution type is not handled.");
			}
		}
		return cost;
	}

	// A welded assembly consists of several subarticles. Every subarticle brings its own
	// worksteps, so a coating step can be present more than once for one complaint.
	private sumManufacturerPrice(worksteps: WorkStepDto[]) {
		return worksteps.reduce((sum, w) => sum + w.manufacturerPrice, 0);
	}

	private getManufacturerPrice(complain: ComplaintDto) {
		var worksteps = this.worksteps.filter(
			(w) =>
				w.articleId == complain.part.articleId ||
				complain.part.includedSubarticleIds.some((id) => id === w.articleId)
		);

		var manufacturerPrice = 0;
		worksteps.forEach(
			(w) => (manufacturerPrice += w.manufacturerPrice * complain.numberOfWrongParts)
		);
		manufacturerPrice = Math.round((manufacturerPrice + Number.EPSILON) * 100) / 100;

		// Calculate powder coating price
		var powderCoatingPrice = 0;
		var powderCoatingWorksteps = worksteps.filter((w) => w.name == "Pulverbeschichten");
		if (powderCoatingWorksteps.length > 0) {
			powderCoatingPrice = this.sumManufacturerPrice(powderCoatingWorksteps);
			powderCoatingPrice += this.sumManufacturerPrice(
				worksteps.filter((w) => w.name == "Teilehandling kratzfrei Pulver")
			);
			powderCoatingPrice *= complain.numberOfWrongParts;
		}
		powderCoatingPrice = Math.round((powderCoatingPrice + Number.EPSILON) * 100) / 100;

		// Calculate Feuerverzinken
		let hotDipZincCoatingPrice = 0;
		let hotDipSteps = worksteps.filter((w) => w.name == "Feuerverzinken");
		if (hotDipSteps.length > 0) {
			hotDipZincCoatingPrice = this.sumManufacturerPrice(hotDipSteps);
			hotDipZincCoatingPrice += this.sumManufacturerPrice(
				worksteps.filter((w) => w.name == "Teilehandling kratzfrei Zink")
			);
			hotDipZincCoatingPrice *= complain.numberOfWrongParts;
		}
		hotDipZincCoatingPrice = Math.round((hotDipZincCoatingPrice + Number.EPSILON) * 100) / 100;

		// Calculate galvanisches Verzinken
		let galvanizeZincCoatingPrice = 0;
		let galvanizeSteps = worksteps.filter((w) => w.name == "Galvanisch verzinken");
		if (galvanizeSteps.length > 0) {
			galvanizeZincCoatingPrice = this.sumManufacturerPrice(galvanizeSteps);
			galvanizeZincCoatingPrice += this.sumManufacturerPrice(
				worksteps.filter((w) => w.name == "Teilehandling kratzfrei Zink")
			);
			galvanizeZincCoatingPrice *= complain.numberOfWrongParts;
		}
		galvanizeZincCoatingPrice =
			Math.round((galvanizeZincCoatingPrice + Number.EPSILON) * 100) / 100;

		var materialPrice = 0;
		worksteps.forEach((w) => (materialPrice += w.materialPrice * complain.numberOfWrongParts));
		materialPrice = Math.round((materialPrice + Number.EPSILON) * 100) / 100;

		const nettoManufacturerPrice =
			manufacturerPrice -
			powderCoatingPrice -
			hotDipZincCoatingPrice -
			galvanizeZincCoatingPrice -
			materialPrice;
		const fertigungPrice = Math.round((nettoManufacturerPrice + Number.EPSILON) * 100) / 100;

		const result = {
			total: manufacturerPrice,
			fertigungPrice: fertigungPrice,
			materialPrice,
			powderCoatingPrice,
			hotDipZincCoatingPrice,
			galvanizeZincCoatingPrice,
		};

		return result;
	}
}
